import { HttpErrorResponse } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {NotificationService} from "./notification.service";

export enum RequestTypesEnum {
  LOGIN = 'LOGIN',
  REGISTRAZIONE = 'REGISTRAZIONE',
  ERRORE_GENERICO = 'ERRORE_GENERICO',
  DELETE_PROFILE = 'DELETE_PROFILE',
  UPDATE_PROFILE = 'UPDATE_PROFILE',
  UPDATE_PASSWORD = 'UPDATE_PASSWORD',
  GET_PROFILE = 'GET_PROFILE',
  ERRORE_RECUPERA_PASSWORD = 'ERRORE_RECUPERA_PASSWORD',
  TIMEOUT = 'TimeoutError',
  OFFLINE = 'OFFLINE',
  GET_FIGLI = 'GET_FIGLI',
  GET_MENU = 'GET_MENU',
  GET_RICARICHE = 'GET_RICARICHE',
  GET_CALENDARIO = 'GET_CALENDARIO',
  ERRORE_AGGIUNTA_EVENTO = 'ERRORE_AGGIUNTA_EVENTO',
  ERRORE_AGGIUNTA_EVENTO_CAUSA_ORARIO = 'ERRORE_AGGIUNTA_EVENTO_CAUSA_ORARIO',
  TOKEN = 'TOKEN',
  POST_RICARICA= 'POST_RICARICA',
  DELETE_RICARICA = 'DELETE_RICARICA',
  RICARICA = 'RICARICA',
  GET_PRATICHE = 'GET_PRATICHE',
  GET_ELIGIBILITA = 'GET_ELIGIBILITA',
  INVIO_ASSISTENZA = 'INVIO_ASSISTENZA',
  VERIFY_ACCOUNT = 'VERIFY_ACCOUNT',
}


@Injectable({
  providedIn: 'root'
})
export class ManageErrorService {

  constructor(
    private notificationService: NotificationService,
    private transloco: TranslocoService,
  ) {
  }

  /** Traduce una chiave del namespace root `common.errors.*` (sempre caricato). */
  private t(key: string): string {
    return this.transloco.translate(`common.errors.${key}`);
  }

  manageError(error: HttpErrorResponse, requestType: RequestTypesEnum) {
    const messageToShow = this.resolveMessage(error, requestType);
    this.notificationService.openToast({message: messageToShow, color: 'danger'})
  }

  /**
   * Fonte unica del testo d'errore (BE-first): se il backend fornisce un messaggio specifico
   * significativo (es. `code 1500`) quello ha precedenza; altrimenti restituisce il testo di
   * fallback centralizzato per il `requestType` dato. NON apre alcun toast: usabile dalle feature
   * (state-machine) per accompagnare l'error-state con un messaggio coerente. Vedi spec `ui-feedback`.
   */
  resolveMessage(error: HttpErrorResponse, requestType: RequestTypesEnum): string {
    const httpStatus: number = error.status;
    const errorCode: number = !!error.error ? error.error.code : null;
    const errorMessage: string = !!error.error ? error.error.message : null;
    const errorType: string = !!error.error ? error.error.error : null;
    return this.getErrorMessage(requestType, httpStatus, errorCode, errorMessage, errorType);
  }

  getErrorMessage(requestType: RequestTypesEnum, httpStatus: number, errorCode: number, errorMessage: string, errorType?: string): string {
    // BE-first: un messaggio applicativo specifico (code 1500) è già risolto e localizzato dal
    // backend e ha sempre la precedenza sui fallback localizzati lato FE.
    if (errorCode === 1500 && !!(errorMessage || '').trim()) {
      return errorMessage;
    }
    switch (requestType) {

      case (RequestTypesEnum.LOGIN):
        return httpStatus === 401 && errorType === 'invalid_grant'
          ? this.t('login_invalid_grant')
          : this.t('login_generic');

      case (RequestTypesEnum.REGISTRAZIONE):
        return httpStatus === 500 && errorCode === 410
          ? this.t('registrazione_email_in_uso')
          : this.t('generic');

      case (RequestTypesEnum.UPDATE_PROFILE):
        return this.t('update_profile');
      case (RequestTypesEnum.DELETE_PROFILE):
        return this.t('delete_profile');
      case (RequestTypesEnum.GET_PROFILE):
        return this.t('get_profile');
      case (RequestTypesEnum.UPDATE_PASSWORD):
        return this.t('update_password');
      case (RequestTypesEnum.VERIFY_ACCOUNT):
        return this.t('verify_account');
      case (RequestTypesEnum.ERRORE_RECUPERA_PASSWORD):
        return errorCode === 411
          ? this.t('recupera_password_email_errata')
          : this.t('recupera_password_generic');
      case (RequestTypesEnum.TIMEOUT):
        return this.t('timeout');
      case(RequestTypesEnum.GET_FIGLI):
        return this.t('get_figli');
      case(RequestTypesEnum.GET_MENU):
        return this.t('get_menu');
      case(RequestTypesEnum.GET_RICARICHE):
        return this.t('get_ricariche');
      case(RequestTypesEnum.ERRORE_AGGIUNTA_EVENTO):
        return this.t('aggiunta_evento');
      case(RequestTypesEnum.ERRORE_AGGIUNTA_EVENTO_CAUSA_ORARIO):
        return this.t('aggiunta_evento_oltre_orario');
      case(RequestTypesEnum.GET_CALENDARIO):
        return this.t('get_calendario');
      case( RequestTypesEnum.TOKEN):
        return this.t('token');
      case(RequestTypesEnum.GET_PRATICHE):
        return this.t('get_pratiche');
      case(RequestTypesEnum.GET_ELIGIBILITA):
        return this.t('get_eligibilita');
      case (RequestTypesEnum.ERRORE_GENERICO):
        return this.t('data_load_error');
      case RequestTypesEnum.DELETE_RICARICA:
        return this.t('delete_ricarica');
      case RequestTypesEnum.POST_RICARICA:
        return this.t('post_ricarica');
      case RequestTypesEnum.RICARICA:
        // Il caso 1500 è già gestito dallo short-circuit BE-first in testa al metodo.
        return this.t('ricarica');
      case RequestTypesEnum.INVIO_ASSISTENZA:
        // Gli errori applicativi dell'assistenza (selettore ambiguo, email reply-to non valida,
        // email assistenza non configurata, entità non trovata, invio SMTP fallito) arrivano
        // GIÀ risolti e localizzati nel `message` del body. Il 404 è privacy-preserving: il
        // messaggio del BE è generico, quindi mostrarlo NON espone il motivo. Preferiamo quel
        // testo quando presente; altrimenti fallback generico con invito al retry.
        if (!!(errorMessage || '').trim()) {
          return errorMessage;
        }
        return this.t('invio_assistenza');
      default:
        return this.t('generic');
    }
  }


}
